import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth.js";
import { COLORS } from "./ui.js";
import Login from "./pages/Login.js";
import Signup from "./pages/Signup.js";
import Dashboard from "./pages/Dashboard.js";
import StripeConnect from "./pages/StripeConnect.js";
import Library from "./pages/Library.js";
import Courses from "./pages/Courses.js";
import CourseDetail from "./pages/CourseDetail.js";
import LessonEditor from "./pages/LessonEditor.js";
import Offers from "./pages/Offers.js";
import SalesPages from "./pages/SalesPages.js";
import Builder from "./builder/Builder.js";

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}

function Shell() {
  const { me, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "system-ui, sans-serif",
          padding: "20vh 0",
          textAlign: "center",
          color: COLORS.muted,
        }}
      >
        Chargement…
      </div>
    );
  }

  const publicRoute = location.pathname === "/login" || location.pathname === "/signup";

  if (!me && !publicRoute) {
    return <Navigate to="/login" replace />;
  }

  if (publicRoute) {
    return (
      <div style={{ minHeight: "100vh", background: COLORS.bg }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
        </Routes>
      </div>
    );
  }

  // Le Builder et LessonEditor sont plein écran sans Header.
  const fullScreen =
    /^\/sales-pages\/[^/]+\/edit$/.test(location.pathname) ||
    /^\/lessons\/[^/]+\/edit$/.test(location.pathname);

  if (fullScreen) {
    return (
      <Routes>
        <Route path="/sales-pages/:id/edit" element={<Builder />} />
        <Route path="/lessons/:id/edit" element={<LessonEditor />} />
      </Routes>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        fontFamily: "system-ui, sans-serif",
        color: COLORS.text,
      }}
    >
      <Header />
      <main style={{ maxWidth: 980, margin: "0 auto", padding: "32px 20px" }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/library" element={<Library />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/sales-pages" element={<SalesPages />} />
          <Route path="/stripe" element={<StripeConnect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Header() {
  const { me, logout } = useAuth();
  return (
    <header
      style={{
        background: "#fff",
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "14px 0",
      }}
    >
      <div
        style={{
          maxWidth: 980,
          margin: "0 auto",
          padding: "0 20px",
          display: "flex",
          alignItems: "center",
          gap: 24,
        }}
      >
        <Link
          to="/"
          style={{
            fontWeight: 700,
            fontSize: 18,
            color: COLORS.text,
            textDecoration: "none",
          }}
        >
          Alchimie
        </Link>
        <nav style={{ display: "flex", gap: 18, flex: 1 }}>
          <NavLink to="/courses">Cours</NavLink>
          <NavLink to="/library">Bibliothèque</NavLink>
          <NavLink to="/offers">Offres</NavLink>
          <NavLink to="/sales-pages">Pages de vente</NavLink>
          <NavLink to="/stripe">Paiements</NavLink>
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, color: COLORS.muted }}>{me?.formateur.displayName}</span>
          <button
            onClick={logout}
            style={{
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              padding: "6px 12px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "inherit",
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        color: COLORS.text,
        textDecoration: "none",
        fontSize: 14,
        fontWeight: 500,
        padding: "4px 0",
      }}
    >
      {children}
    </Link>
  );
}
